import { useEffect, useState, useRef } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
    Database, Upload, Loader2, Trash2, CheckCircle2, AlertTriangle,
    FileJson, RefreshCw,
} from 'lucide-react';

/**
 * Admin → KP Fixtures upload page.
 *
 * The KP regression backtest dataset (Set 1 + Set 2) used to live in `/tmp`,
 * which is wiped on every fork. This page lets the operator upload JSON
 * fixtures into `/app/backend/data/` (persistent across pod restarts) so the
 * `scripts/run_backtest.py` dashboard remains reproducible.
 *
 * Backend routes used (all gated by is_admin):
 *   GET    /api/admin/kp-fixtures             → list both sets w/ metadata
 *   POST   /api/admin/kp-fixtures/{name}      → multipart upload (file=...)
 *   DELETE /api/admin/kp-fixtures/{name}      → remove a persistent fixture
 */
const SETS = [
    { name: 'kp_cases', label: 'Set 1 — KP Reader VI', hint: 'list-of-lists (raw spreadsheet rows, ≥ 9 columns)' },
    { name: 'kp_set2',  label: 'Set 2 — KP Reader II',  hint: 'list-of-dicts keyed by "Case No.", "Chapter", "Query / Question", "Date of Judgement" …' },
];

export default function AdminKpFixtures() {
    const [fixtures, setFixtures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);   // currently-uploading set name

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('/admin/kp-fixtures');
            setFixtures(r.data.fixtures);
        } catch (e) {
            toast.error(e?.userMessage || 'Failed to load fixtures');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="admin-kp-fixtures-loading">
                <Loader2 className="animate-spin text-marigold" size={24} />
            </div>
        );
    }

    return (
        <div className="p-6 lg:p-10 space-y-8" data-testid="admin-kp-fixtures-page">
            <header className="flex items-start justify-between gap-4">
                <div>
                    <div className="overline">Regression dataset</div>
                    <h1 className="font-serif text-3xl text-parchment mt-2 flex items-center gap-3">
                        <Database size={28} className="text-marigold" /> KP Fixtures
                    </h1>
                    <p className="text-sm text-slate-400 mt-2 max-w-2xl">
                        Upload the KP-book backtest JSONs into the persistent data folder
                        (<code className="text-marigold/90">/app/backend/data/</code>).
                        Survives forks and pod restarts so the regression dashboard never goes
                        cold after a redeploy.
                    </p>
                </div>
                <Button
                    onClick={load}
                    variant="ghost"
                    size="sm"
                    data-testid="kp-fixtures-refresh"
                    className="text-slate-400 hover:text-parchment"
                >
                    <RefreshCw size={14} className="mr-2" /> Refresh
                </Button>
            </header>

            <section className="grid gap-4 lg:grid-cols-2" data-testid="kp-fixtures-grid">
                {SETS.map(({ name, label, hint }) => {
                    const meta = fixtures.find((f) => f.name === name) || {};
                    return (
                        <FixtureCard
                            key={name}
                            name={name}
                            label={label}
                            hint={hint}
                            meta={meta}
                            busy={busy === name}
                            setBusy={setBusy}
                            reload={load}
                        />
                    );
                })}
            </section>

            <Card className="p-5 bg-surface/40 border-marigold/10" data-testid="kp-fixtures-howto">
                <div className="flex items-start gap-3">
                    <FileJson size={18} className="text-marigold mt-1" />
                    <div className="text-sm text-slate-300 space-y-2">
                        <div className="font-semibold text-parchment">How to run the regression dashboard</div>
                        <pre className="text-xs bg-obsidian/60 border border-marigold/10 rounded-sm p-3 overflow-x-auto">
PYTHONPATH=/app/backend python3 /app/backend/scripts/run_backtest.py
                        </pre>
                        <div className="text-slate-400">
                            Output is appended to <code className="text-marigold/90">/app/test_reports/kp_backtest_&lt;ts&gt;.json</code>
                            and also saved as <code className="text-marigold/90">kp_backtest_latest.json</code> for diffing.
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
}


function FixtureCard({ name, label, hint, meta, busy, setBusy, reload }) {
    const fileRef = useRef(null);
    const present = meta.persistent_exists || meta.legacy_tmp_exists;
    const onPersistent = !!meta.persistent_exists;
    const cases = meta.case_count;
    const size = meta.size_bytes;
    const updated = meta.updated_at;

    const onPick = () => fileRef.current?.click();

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.name.toLowerCase().endsWith('.json')) {
            toast.error('Only .json files are accepted');
            e.target.value = '';
            return;
        }
        const form = new FormData();
        form.append('file', file);
        setBusy(name);
        try {
            const r = await api.post(`/admin/kp-fixtures/${name}`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success(`Uploaded ${name}: ${r.data.case_count} cases`);
            await reload();
        } catch (err) {
            toast.error(err?.userMessage || 'Upload failed');
        } finally {
            setBusy(null);
            e.target.value = '';
        }
    };

    const onDelete = async () => {
        if (!window.confirm(`Delete persistent fixture for ${name}?`)) return;
        setBusy(name);
        try {
            await api.delete(`/admin/kp-fixtures/${name}`);
            toast.success(`Deleted ${name}`);
            await reload();
        } catch (err) {
            toast.error(err?.userMessage || 'Delete failed');
        } finally {
            setBusy(null);
        }
    };

    return (
        <Card className="p-5 bg-surface/40 border-marigold/10 space-y-4" data-testid={`kp-card-${name}`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="overline">{name}</div>
                    <div className="font-serif text-xl text-parchment mt-1">{label}</div>
                    <div className="text-xs text-slate-500 mt-1.5">{hint}</div>
                </div>
                <FixtureStatusBadge present={present} onPersistent={onPersistent} />
            </div>

            <div className="grid grid-cols-3 gap-3 text-xs">
                <Stat label="Cases" value={cases ?? '—'} testId={`kp-cases-${name}`} />
                <Stat label="Size" value={size != null ? formatBytes(size) : '—'} testId={`kp-size-${name}`} />
                <Stat label="Updated" value={updated ? new Date(updated).toLocaleString() : '—'} testId={`kp-updated-${name}`} />
            </div>

            <div className="flex gap-2 pt-2">
                <input
                    ref={fileRef}
                    type="file"
                    accept="application/json,.json"
                    onChange={onUpload}
                    className="hidden"
                    data-testid={`kp-file-input-${name}`}
                />
                <Button
                    onClick={onPick}
                    disabled={busy}
                    size="sm"
                    className="bg-marigold text-obsidian hover:bg-marigold/90"
                    data-testid={`kp-upload-${name}`}
                >
                    {busy ? (
                        <><Loader2 size={14} className="mr-2 animate-spin" /> Uploading…</>
                    ) : (
                        <><Upload size={14} className="mr-2" /> {present ? 'Replace JSON' : 'Upload JSON'}</>
                    )}
                </Button>
                {onPersistent && (
                    <Button
                        onClick={onDelete}
                        disabled={busy}
                        size="sm"
                        variant="ghost"
                        className="text-slate-400 hover:text-red-300"
                        data-testid={`kp-delete-${name}`}
                    >
                        <Trash2 size={14} className="mr-2" /> Delete
                    </Button>
                )}
            </div>
        </Card>
    );
}

function FixtureStatusBadge({ present, onPersistent }) {
    if (!present) {
        return (
            <Badge variant="outline" className="border-red-500/40 text-red-300" data-testid="kp-status-missing">
                <AlertTriangle size={12} className="mr-1" /> Missing
            </Badge>
        );
    }
    if (onPersistent) {
        return (
            <Badge className="bg-green-600/20 text-green-300 border border-green-500/30" data-testid="kp-status-persistent">
                <CheckCircle2 size={12} className="mr-1" /> Persistent
            </Badge>
        );
    }
    return (
        <Badge variant="outline" className="border-amber-500/40 text-amber-300" data-testid="kp-status-legacy">
            /tmp only — re-upload to persist
        </Badge>
    );
}

function Stat({ label, value, testId }) {
    return (
        <div data-testid={testId}>
            <div className="text-slate-500 uppercase tracking-wider">{label}</div>
            <div className="text-parchment mt-0.5 truncate" title={String(value)}>{value}</div>
        </div>
    );
}

function formatBytes(b) {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
}
